/**
 * @file capture/useMicDevices.ts
 * @brief Browser mic enumeration for the boardroom picker.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useCallback, useEffect, useState } from "react";
import { describeMicError } from "./micErrors";

export type MicDevice = {
  deviceId: string;
  label: string;
  groupId: string;
};

/**
 * (05-10-2026) Drops "Verblizr Microphone", the desktop app's virtual mic for
 * interpretation Call mode. Recording or enrolling through it routes your
 * voice through the pass-through instead of straight from the mic (DES: a
 * Teams meeting sounded "robotic and laggy"). "System default" stays: outside
 * Call mode the helper never lets ours be the Mac's default.
 */
export function withoutVerblizrMic(devices: MicDevice[]): MicDevice[] {
  return devices.filter((d) => d.deviceId === "default" || !/verblizr/i.test(d.label));
}

export function useMicDevices(opts: { hideVerblizrMic?: boolean } = {}) {
  const hideVerblizrMic = opts.hideVerblizrMic === true;
  const [devices, setDevices] = useState<MicDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setDevices([]);
      setError("This browser cannot list microphones.");
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      // Labels stay empty until permission has been granted once.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      const all = await navigator.mediaDevices.enumerateDevices();
      const listed = all
        .filter((d) => d.kind === "audioinput")
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label?.trim() || `Microphone ${i + 1}`,
          groupId: d.groupId,
        }));
      const mics = hideVerblizrMic ? withoutVerblizrMic(listed) : listed;
      setDevices(mics);
      setError(null);
      return mics;
    } catch (err) {
      setError(describeMicError(err));
      setDevices([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [hideVerblizrMic]);

  useEffect(() => {
    void refresh();
    const handler = () => {
      void refresh();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () => {
      navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
    };
  }, [refresh]);

  return { devices, error, loading, refresh };
}
